import { Button, Flex, Modal, Text } from '@conteg/ui';
import AccessibilityRequirements from 'components/accessibility-requirements/accessibility-requirements';
import StorageUnitFeaturesDetail from 'components/storage-unit-features-detail/storage-unit-features-detail';
import { DateTime } from 'luxon';
import ReservationLanguageDetail from 'pages/storage-unit-detail/components/actions/reservation-language-detail/reservation-language-detail';
import { TwoFactorWarning } from 'pages/storage-unit-detail/components/actions/two-factor-warning';
import { getEmails } from 'pages/storage-unit-detail/utils/get-emails';
import { getPhoneNumbers } from 'pages/storage-unit-detail/utils/get-phone-numbers';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BlockingReservationReason,
  ServiceType,
  StorageUnitDtoFragment,
} from 'types/generated/graphql';
import { useChangeReservationDurationMode } from 'utils/hooks/use-change-reservation-duration-mode';
import { formatDateTime, TimeFormats } from 'utils/time/format-time';

import ChangeReservationDuration from '../actions/change-reservation-duration/change-reservation-duration';

type ReservationDetailProps = {
  storageUnit: StorageUnitDtoFragment;
};

const AllocationDetail = ({ storageUnit }: ReservationDetailProps) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<boolean>(false);

  // An allocated parcel still waiting for its two-factor code: shortening the window would release it
  // without the code, so the dedicated role decides.
  const changeReservationDurationMode = useChangeReservationDurationMode(
    !!storageUnit?.allocation?.reservation?.is2FaRequired &&
      !storageUnit?.allocation?.reservation?.twoFactorVerifiedAt
  );

  const allocation = storageUnit?.allocation;
  const hasBlockingReservation =
    storageUnit?.allocation?.reservation?.isBlockingReservation;
  const trackingIdentifiers = allocation?.contents
    ?.flatMap((content) => content.trackingIdentifiers ?? [])
    .filter((ti) => ti.identifier)
    .map((ti) => ti.identifier)
    .join(', ');

  const { phoneNumber, senderPhoneNumber } = getPhoneNumbers(
    allocation?.reservation?.customerPinCredentials,
    allocation?.reservation.serviceType
  );

  const { email, senderEmail } = getEmails(
    allocation?.reservation?.customerPinCredentials,
    allocation?.reservation.serviceType
  );

  const reservationId = allocation?.reservation?.id as string;
  const isDeliverySubmission = allocation?.reservation?.isDeliverySubmission;
  const blockingReservationReason =
    allocation?.reservation?.blockingReservationReason;
  const blockingReservationReasonDescription =
    allocation?.reservation?.blockingReservationReasonDescription;
  const requiredStorageUnitFeatures = Array.from(
    new Set(
      (allocation?.contents ?? []).flatMap(
        (content) => content.requiredStorageUnitFeatures ?? []
      )
    )
  );
  const language = allocation?.reservation.languageCode;
  const serviceType = allocation?.reservation.serviceType;
  const isNeverEndReservation = storageUnit.isExistingReservationNeverEnding;

  // Accessibility information from reservation
  const requestedEasyAccessible =
    allocation?.reservation?.requestedEasyAccessibleStorageUnit;
  const assignedToEasyAccessible =
    allocation?.reservation?.assignedToEasyAccessibleStorageUnit;

  const isReservationForStockout = useCallback(() => {
    if (!allocation?.reservation?.to) return;

    const currentTime = DateTime.now();
    const reservationEndTime = DateTime.fromISO(allocation.reservation.to);

    return (
      currentTime > reservationEndTime ||
      ((blockingReservationReason ==
        BlockingReservationReason.UnidentifiedContent ||
        blockingReservationReason == BlockingReservationReason.WrongContent) &&
        serviceType == ServiceType.Delivery)
    );
  }, [allocation?.reservation?.to, serviceType, blockingReservationReason]);

  return (
    <Flex flexDirection="column" gap="3rem">
      {isNeverEndReservation && (
        <Flex alignItems="center" gap="3rem">
          <Text
            variant="highlight"
            content={t('Page.Reservation.NeverEndingReservation')}
          />
        </Flex>
      )}

      {!hasBlockingReservation && trackingIdentifiers !== undefined && (
        <Flex alignItems="center" gap="2rem">
          <Text
            variant="highlight"
            content={t('Page.Reservation.Id', {
              trackingIdentifier: trackingIdentifiers,
              allocationType: isDeliverySubmission
                ? t('Page.Reservation.IsDeliverySubmission')
                : t('Page.Reservation.CommonAllocation'),
            })}
          />
        </Flex>
      )}
      {(senderPhoneNumber || phoneNumber) && (
        <Flex flexDirection="column">
          <Text
            variant="highlight"
            content={t('Page.Reservation.PhoneNumber')}
          />
          {serviceType === ServiceType.HandToHand ? (
            <>
              {senderPhoneNumber && (
                <Flex gap="1rem">
                  <Text
                    variant="highlight"
                    content={`${t('Page.Reservation.PhoneNumber.Sender')}: `}
                  />
                  <Text content={senderPhoneNumber} />
                </Flex>
              )}
              {phoneNumber && (
                <Flex gap="1rem">
                  <Text
                    variant="highlight"
                    content={`${t('Page.Reservation.PhoneNumber.Recipient')}: `}
                  />
                  <Text content={phoneNumber} />
                </Flex>
              )}
            </>
          ) : (
            phoneNumber && <Text content={phoneNumber} />
          )}
        </Flex>
      )}
      {(senderEmail || email) && (
        <Flex flexDirection="column">
          <Text variant="highlight" content={t('Page.Reservation.Email')} />
          {serviceType === ServiceType.HandToHand ? (
            <>
              {senderEmail && (
                <Flex gap="1rem">
                  <Text
                    variant="highlight"
                    content={`${t('Page.Reservation.Email.Sender')}: `}
                  />
                  <Text content={senderEmail} />
                </Flex>
              )}
              {email && (
                <Flex gap="1rem">
                  <Text
                    variant="highlight"
                    content={`${t('Page.Reservation.Email.Recipient')}: `}
                  />
                  <Text content={email} />
                </Flex>
              )}
            </>
          ) : (
            email && <Text content={email} />
          )}
        </Flex>
      )}
      {isReservationForStockout() && (
        <Text variant="highlight" content={t('Page.Reservation.Expired')} />
      )}
      {hasBlockingReservation && (
        <Text
          variant="highlight"
          content={t('Page.Reservation.BlockingReservation', {
            blockingReservationReason: t(blockingReservationReason ?? ''),
          })}
        />
      )}
      {blockingReservationReasonDescription && (
        <Text
          variant="highlight"
          content={t('Page.Reservation.BlockingReservationReasonDescription', {
            blockingReservationReasonDescription,
          })}
        />
      )}
      {allocation && !isNeverEndReservation && (
        <Text
          variant="large"
          content={t('Page.Reservation.Times', {
            start: formatDateTime(
              allocation?.reservation?.from,
              TimeFormats.dateTime
            ),
            end: formatDateTime(
              allocation?.reservation?.to,
              TimeFormats.dateTime
            ),
          })}
        />
      )}
      {allocation && !isNeverEndReservation && (
        <Flex gap="4rem" alignItems="center">
          <Text
            variant="large"
            content={t('Page.Reservation.TimesStockIn', {
              start: formatDateTime(
                allocation?.reservation?.expiration,
                TimeFormats.dateTime
              ),
            })}
          />
          {changeReservationDurationMode && (
            <>
              <Button
                testId="button-open-modal-update-reservation-duration"
                variant="primary"
                size="s"
                title={t(
                  changeReservationDurationMode === 'bidirectional'
                    ? 'Button.ChangeReservationDuration'
                    : 'Button.UpdateReservationDuration'
                )}
                onClick={() => setOpenModal(true)}
              />
              <Modal
                testId="unit-actions-modal"
                size="l"
                showCloseButton={true}
                isOpen={openModal}
                onClose={() => setOpenModal(false)}
              >
                <TwoFactorWarning
                  isTwoFactorPending={
                    !!allocation?.reservation?.is2FaRequired &&
                    !allocation?.reservation?.twoFactorVerifiedAt
                  }
                  failedAttempts={
                    allocation?.reservation?.twoFactorFailedAttempts ??
                    undefined
                  }
                />
                <ChangeReservationDuration
                  reservationId={reservationId}
                  from={allocation?.reservation?.from}
                  to={allocation?.reservation?.to}
                  mode={changeReservationDurationMode}
                />
              </Modal>
            </>
          )}
        </Flex>
      )}
      {allocation && !isNeverEndReservation && (
        <Text
          variant="large"
          content={t('Page.Reservation.TimeHandover', {
            time: formatDateTime(
              allocation?.reservation?.to,
              TimeFormats.dateTime
            ),
          })}
        />
      )}
      {language && <ReservationLanguageDetail languageCode={language} />}
      {requiredStorageUnitFeatures.length > 0 && (
        <StorageUnitFeaturesDetail
          onReservation={true}
          storageUnitFeatures={requiredStorageUnitFeatures}
        />
      )}

      <AccessibilityRequirements
        easyAccessibleRequested={requestedEasyAccessible}
        easyAccessibleFulfilled={assignedToEasyAccessible}
      />

      <Text
        variant="large"
        content={
          <>
            <strong>{t('Page.Reservation.Company')}:</strong>
            {t('Page.Reservation.Company.Info', {
              name: storageUnit.company?.name,
              contact: storageUnit.phoneNumber,
            })}
          </>
        }
      />
      <Text
        variant="large"
        content={
          <>
            <strong>{t('Page.Reservation.ServiceType')}: </strong>
            {t(`Page.Reservation.ServiceType.${serviceType}`)}
          </>
        }
      />
      <Text
        testId="two-factor-requirement"
        variant="large"
        content={
          <>
            <strong>{t('Page.Reservation.TwoFactor')}: </strong>
            {t(
              allocation?.reservation?.is2FaRequired
                ? 'Page.Reservation.TwoFactor.Yes'
                : 'Page.Reservation.TwoFactor.No'
            )}
          </>
        }
      />
    </Flex>
  );
};

export default AllocationDetail;
